import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AuthUser } from "@shoe-trace/shared";
import { clearSession, fetchMe, login as loginRequest, logout as logoutRequest, onUnauthorized, readStoredUser, storeSession } from "./api/client";

interface SessionValue {
  user: AuthUser | null;
  ready: boolean;
  expired: boolean;
  login: (employeeNo: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  replaceUser: (user: AuthUser) => void;
  dismissExpired: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(readStoredUser);
  const [ready, setReady] = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const stop = onUnauthorized(() => {
      setUser(null);
      setExpired(true);
    });
    return stop;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!readStoredUser()) {
        if (!cancelled) setReady(true);
        return;
      }
      try {
        const result = await fetchMe();
        if (!cancelled) setUser(result.user);
      } catch {
        if (!cancelled) {
          clearSession();
          setUser(null);
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      user,
      ready,
      expired,
      async login(employeeNo, password) {
        const result = await loginRequest(employeeNo, password);
        storeSession(result.token, result.user);
        setUser(result.user);
        setExpired(false);
        return result.user;
      },
      async logout() {
        try {
          await logoutRequest();
        } catch {
          clearSession();
        }
        clearSession();
        setUser(null);
        setExpired(false);
      },
      replaceUser(next) {
        const current = localStorage.getItem("wms.token");
        if (current) storeSession(current, next);
        setUser(next);
      },
      dismissExpired() {
        setExpired(false);
      },
    }),
    [user, ready, expired]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("会话还没准备好");
  return value;
}

export function canManage(user: AuthUser | null): boolean {
  return user?.role === "SUPERVISOR" || user?.role === "ADMIN";
}
